import { useEffect, useState, useRef, useMemo } from 'react'
import EliteRankGraph from './EliteRankGraph'
import { TextToggle } from '../../ui-components/TextToggle'
import Button from '../../ui-components/Button'
import { EventNames } from '../../../types/misc.types'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { convertEpochToDate, toPascalCaseWithSpaces } from '../../../utils/utils'
import {
  EliteScoreTrends,
  EliteScoresTypes,
  ScoreHistoryList,
  WaterfallSections
} from '../../../types/mutual-fund-detail.types'
import Tabs from '../../ui-components/Tabs'
import RankGraphUnavailable from '../../rank/RankGraphUnavailable'
import { trackEvent } from '../../../utils/appBridge'
import TextAndInfo from '../../ui-components/TextAndInfo'
import { performanceTagMetaData } from '../../../constants'
import { ReturnsScoreHelpContent } from '../../BottomSheetComponents/ReturnsScoreHelpContent'
import { StabilityScoreHelpContent } from '../../BottomSheetComponents/StabilityScoreHelpContent'
import { RecencyScoreHelpContent } from '../../BottomSheetComponents/RecencyScoreHelpContent'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import useAlternateISIN from '../../../hooks/useAlternateISIN'
import { getAppVersion } from '../../../utils/sessionStorage'
import { useEliteBuyCtaExtras } from '../EliteBuyCtaExtrasContext'

type EliteScoreTrendSectionPropTypes = {
  eliteScoreData: ScoreHistoryList | undefined
  eliteScoresType: `${EliteScoresTypes}`
  categoryCount: number
}

export default function EliteScoreTrendSection({
  eliteScoreData,
  eliteScoresType,
  categoryCount
}: EliteScoreTrendSectionPropTypes) {
  const { data: metadata } = useMetaDataContext()
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()

  const { alternateISIN } = useAlternateISIN(WaterfallSections.ELITE)

  const [rankData, setRankData] = useState<ScoreHistoryList>([])
  const [selectedTabId, setSelectedTabId] = useState<string>('6M')
  const [filteredData, setFilteredData] = useState<ScoreHistoryList>([])
  const [showLabels, setShowLabels] = useState(true)
  const scoreTrendRef = useRef<HTMLDivElement>(null)

  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const isInteractionEventLocked = useRef<boolean>(false)

  const bottomSheets = {
    [EliteScoresTypes.CONSISTENCY]: ReturnsScoreHelpContent,
    [EliteScoresTypes.RECENCY]: RecencyScoreHelpContent,
    [EliteScoresTypes.VOLATILITY]: StabilityScoreHelpContent
  }

  const ScreenName = `MF_L2_Elite_${toPascalCaseWithSpaces(eliteScoresType)}_Score`

  useEffect(() => {
    if (eliteScoreData) {
      const dataCopy = eliteScoreData
      let scoreHistoryList: ScoreHistoryList = JSON.parse(JSON.stringify(dataCopy || []))
      const sortedData = scoreHistoryList?.sort((a, b) => (b?.x_value ?? 0) - (a?.x_value ?? 0))
      if (scoreHistoryList?.length < 25 && scoreHistoryList?.length > 0) {
        let lastPointMonthDate = new Date(sortedData?.slice(-1)?.[0]?.x_value)
        let remainingLength = 25 - sortedData?.length
        while (remainingLength > 0) {
          const lastPointMonthDateCopy = new Date(JSON.parse(JSON.stringify(lastPointMonthDate))) as Date
          const prevMonth = new Date(lastPointMonthDateCopy?.setMonth(lastPointMonthDateCopy?.getMonth() - 1))
          const prevMonthTime = prevMonth?.getTime()
          const prevMothPoint = {
            x_value: prevMonthTime,
            y_value: 40,
            tag: EliteScoreTrends.AVERAGE,
            label: -1
          } as ScoreHistoryList[0]
          sortedData?.push(prevMothPoint)
          lastPointMonthDate = new Date(prevMonthTime)
          remainingLength--
        }
      }
      scoreHistoryList = sortedData?.sort((a, b) => (a?.x_value ?? 0) - (b?.x_value ?? 0))
      setRankData(() => scoreHistoryList)
      setFilteredData(() => scoreHistoryList?.slice(-7)) // Default to 7 values
    }
  }, [eliteScoreData])

  const lastRankDatum = useMemo(() => {
    return rankData?.slice(-1)?.[0] ?? null
  }, [rankData])

  useEffect(() => {
    const sliceCount =
      {
        '6M': 7,
        '1Y': 13,
        '2Y': 25
      }[selectedTabId] ?? 0
    if (selectedTabId) {
      const updatedData = rankData?.slice(-sliceCount) || []
      setFilteredData(updatedData)
    }
  }, [selectedTabId, rankData])

  const getNormalizedYValue = (performanceDatum: ScoreHistoryList[0]) => {
    const { projectedRange, actualRange } = performanceTagMetaData?.[performanceDatum?.tag]
    const yValue = !isNaN(+performanceDatum.label) ? +performanceDatum.label : -1

    const [aMin, aMax] = actualRange
    const [pMin, pMax] = projectedRange

    const clampedY = Math.max(aMin, Math.min(aMax, yValue))
    const normalizedYValue = ((clampedY - aMin) / (aMax - aMin)) * (pMax - pMin) + pMin

    return normalizedYValue
  }

  const convertData = (dataList: ScoreHistoryList) => {
    const sampleData: {
      month: number
      value: number
      label: string
      tag: `${EliteScoreTrends}`
      isVisible: boolean
    }[] = []
    dataList.forEach((data) => {
      sampleData.push({
        month: data.x_value,
        value: getNormalizedYValue(data),
        tag: data?.tag,
        label: `${data.label}`,
        isVisible: data?.label !== -1
      })
    })
    return sampleData
  }

  const tabsData = [
    { id: '6M', label: '6M' },
    { id: '1Y', label: '1Y' },
    { id: '2Y', label: '2Y' }
  ].filter((tab, index) => index < rankData?.length / 7)

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ...eliteBuyCtaExtras,
        eventId: 'Score',
        ScreenName,
        fundName: alternateISIN,
        screenType: 'MF_L2'
      }
    })
  }, [])

  const getScoreDisplayName = (scoreType: `${EliteScoresTypes}`) => {
    const appVersion = getAppVersion()

    switch (scoreType) {
      case EliteScoresTypes.CONSISTENCY:
        return appVersion >= 47 ? 'Consistency Score' : 'Returns Score'
      case EliteScoresTypes.VOLATILITY:
        return appVersion >= 47 ? 'Volatility Score' : 'Stability Score'
      case EliteScoresTypes.RECENCY:
        return 'Recency Score'
      default:
        return toPascalCaseWithSpaces(scoreType) + ' Score'
    }
  }

  return (
    <div className='flex flex-col w-full pb-6'>
      <div className={`flex justify-between gap-1 items-center pb-4`}>
        <div className='flex flex-col flex-1'>
          <div className='flex items-center'>
            <div className='text-black font-[500] pb-[1px]'>{getScoreDisplayName(eliteScoresType)}</div>
            <div>
              <TextAndInfo
                text={``}
                textClassName='!text-[#6A6E6E] !text-[13px] !font-[400]'
                onInfoClick={(event) => {
                  event.stopPropagation()
                  trackEvent({
                    eventName: EventNames.UpdateInfoButton,
                    payload: {
                      ...eliteBuyCtaExtras,
                      ScreenName,
                      fundName: alternateISIN,
                      screenType: 'MF_L2'
                    }
                  })
                  const BottomSheet = bottomSheets?.[eliteScoresType]
                  openBottomSheet(<BottomSheet onDismiss={closeBottomSheet} />)
                }}
              />
            </div>
          </div>

          <div className='flex gap-[4px] text-[#3D3D3D]'>
            <div>in {toPascalCaseWithSpaces(metadata?.fundDetails?.category ?? 'NA')}</div>
          </div>
        </div>
        <div className='flex flex-col pb-1'>
          <div className='text-end pb-1'>
            {categoryCount ? (
              <>
                <span className='text-black text-[20px] font-[700]'>{lastRankDatum?.label}</span>
                <span className='text-[#6A6E6E] text-[16px] font-[400]'>/100</span>
              </>
            ) : (
              <span className='text-black text-[20px] font-[700]'>NA</span>
            )}
          </div>
          {lastRankDatum && (
            <div
              style={{ background: performanceTagMetaData?.[lastRankDatum?.tag]?.color }}
              className='text-black font-[500] text-[13px] text-end rounded-[8px] px-[6px]'
            >
              {performanceTagMetaData?.[lastRankDatum?.tag]?.label}{' '}
              <span className='opacity-60 font-[400]'> in {convertEpochToDate(lastRankDatum?.x_value, '%B')}</span>
            </div>
          )}
        </div>
      </div>
      <hr className='bg-[#EBF2F2] opacity-70' />

      <div className='flex flex-col'>
        <div
          ref={scoreTrendRef}
          className={`text-[#6A6E6E] pt-4 pb-2 pl-1 text-13 ${filteredData?.length <= 0 ? '' : 'mb-[-32px]'}`}
        >
          Score Trend
        </div>
        <div>
          {filteredData?.length > 0 && categoryCount > 0 ? (
            <EliteRankGraph
              data={convertData(filteredData)}
              showLabels={showLabels}
              categoryCount={categoryCount ?? 0}
              onScoreChange={(score: string | number, date: any) => {
                const isDateToday = date === 'Today'
                if (!isInteractionEventLocked?.current && !isDateToday) {
                  trackEvent({
                    eventName: EventNames.GraphInteraction,
                    payload: {
                      ...eliteBuyCtaExtras,
                      eventId: 'slider_move',
                      responseName: `${selectedTabId}`,
                      ScreenName,
                      fundName: alternateISIN,
                      screenType: 'MF_L2'
                    }
                  })
                  isInteractionEventLocked.current = true
                }
                if (scoreTrendRef && scoreTrendRef?.current)
                  scoreTrendRef.current.style.visibility = isDateToday ? 'visible' : 'hidden'
              }}
            />
          ) : (
            <RankGraphUnavailable />
          )}
        </div>
      </div>
      {filteredData?.length > 0 && (
        <div className='flex gap-3 pt-4'>
          <div className='flex-1'>
            <Tabs
              isFullWidth
              tabsData={tabsData}
              onTabClick={(tabId: any) => {
                trackEvent({
                  eventName: EventNames.GraphInteraction,
                  payload: {
                    ...eliteBuyCtaExtras,
                    eventId: 'graph_period',
                    responseName: tabId?.split(' ')?.[0],
                    ScreenName,
                    fundName: alternateISIN,
                    screenType: 'MF_L2'
                  }
                })
                setSelectedTabId(tabId)
              }}
              selectedTabId={selectedTabId}
            />
          </div>
          {/* <div className='w-[85px]'>
            <Button isFullWidth onClick={() => null} buttonClassName='outline-none min-h-9'>
              <TextToggle
                currentToggleOption={'Score'}
                toggleOptions={['Rank', 'Score']}
                onToggleClick={() => {
                  trackEvent({
                    eventName: EventNames.GraphInteraction,
                    payload: {
        ...eliteBuyCtaExtras,
                      eventId: 'graph_toggle',
                      responseName: showLabels ? 'Score' : 'PowerRank',
                      ScreenName,
                      fundName: alternateISIN,
                      screenType: 'MF_L2'
                    }
                  })
                  setShowLabels(!showLabels)
                }}
                toggleColor='#232323'
                toggleClassName='font-[500]'
              />
            </Button>
          </div> */}
          {/* <div className='w-[38px]'>
            <Button onClick={() => null} buttonClassName='min-w-[20px] w-[38px] min-h-9'>
              <img src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/help-icon.svg`} />
            </Button>
          </div> */}
        </div>
      )}
      <div className='pt-6 text-[#6A6E6E] text-[13px]'>Updates every month</div>

      {/* <VaulDrawer open={openBottomSheet} setOpen={setOpenBottomSheet}>
        {renderBottomSheetContent()}
      </VaulDrawer> */}
    </div>
  )
}
